import React, { useEffect, useState } from 'react';
import { ExternalLink, ImagePlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, errMsg } from '../api';
import { Card, SectionTitle, goldBtn } from '../ui';
import WbQuestionsEditor from './WbQuestionsEditor';

const input = 'w-full bg-transparent border border-[#2A2F37] p-3 text-sm';

function LogoField({ isOwner }) {
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);
  const fetchLogo = () => api.get('/kit/logo', { responseType: 'blob' }).then((r) => setLogo(URL.createObjectURL(r.data))).catch(() => setLogo(null));
  useEffect(() => { fetchLogo(); }, []);
  const upload = async (file) => {
    const fd = new FormData(); fd.append('file', file); setBusy(true);
    try { await api.post('/kit/logo', fd); toast.success('Logo caricato'); fetchLogo(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="wb-page-logo">
      {logo ? <img src={logo} alt="Logo aziendale" className="h-12 max-w-[180px] object-contain bg-white p-1" data-testid="wb-page-logo-preview" /> : <span className="text-sm text-[#7A7F87]">Nessun logo: sulla pagina compare il marchio BOND.</span>}
      {isOwner && <label className="h-9 px-3 border border-[#2A2F37] inline-flex items-center gap-1 cursor-pointer text-sm hover:border-[#D4AF37]" data-testid="wb-page-logo-upload">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}{logo ? 'Cambia logo' : 'Carica logo (PNG o JPG)'}
        <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => { if (e.target.files[0]) upload(e.target.files[0]); e.target.value = ''; }} data-testid="wb-page-logo-input" />
      </label>}
    </div>
  );
}

export default function WbPageEditor({ data, isOwner, reload }) {
  const ch = data.channel;
  const [f, setF] = useState({ company: ch.company || '', intro: ch.intro || '', questions: ch.questions || [] });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put('/whistleblowing/channel', { company: f.company, intro: f.intro, questions: f.questions.map((q) => ({ ...q, options: q.options.filter(Boolean) })) });
      toast.success('Pagina aggiornata'); reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="p-6 space-y-5" data-testid="wb-page-editor">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><SectionTitle>Pagina pubblica di segnalazione</SectionTitle><p className="text-sm text-[#8A8F97] -mt-2">Personalizza quello che vedono dipendenti e collaboratori quando aprono il canale.</p></div>
        <a href={data.public_url} target="_blank" rel="noreferrer" className="h-10 px-4 inline-flex items-center gap-2 text-sm border border-[#2A2F37] hover:border-[#D4AF37] hover:text-[#D4AF37]" data-testid="wb-page-preview">Apri la pagina<ExternalLink size={14} /></a>
      </div>
      <div className="space-y-2"><p className="text-[11px] uppercase tracking-[0.15em] text-[#7A7F87]">Logo</p><LogoField isOwner={isOwner} /></div>
      <label className="block space-y-2"><span className="text-[11px] uppercase tracking-[0.15em] text-[#7A7F87]">Nome azienda mostrato</span>
        <input value={f.company} disabled={!isOwner} onChange={(e) => setF({ ...f, company: e.target.value })} className={input} data-testid="wb-page-company" /></label>
      <label className="block space-y-2"><span className="text-[11px] uppercase tracking-[0.15em] text-[#7A7F87]">Testo di benvenuto</span>
        <textarea rows={4} value={f.intro} disabled={!isOwner} onChange={(e) => setF({ ...f, intro: e.target.value })} placeholder="Se lo lasci vuoto viene mostrato un testo standard sul canale riservato e anonimo." className={input} data-testid="wb-channel-intro" /></label>
      {isOwner && <WbQuestionsEditor value={f.questions} onChange={(questions) => setF({ ...f, questions })} />}
      {isOwner
        ? <button disabled={busy || f.company.trim().length < 2} onClick={save} className={`${goldBtn} h-10 px-5 text-sm inline-flex items-center gap-2 disabled:opacity-40`} data-testid="wb-page-save">{busy && <Loader2 size={14} className="animate-spin" />}Salva la pagina</button>
        : <p className="text-xs text-[#7A7F87]">Solo il titolare dell'account può modificare la pagina.</p>}
    </Card>
  );
}
